import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { Values } from '../../../forms/CompanyBankAccounts/CompanyBankAccount.types';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CompanyBankAccountForm } from '../../../forms/CompanyBankAccounts/CompanyBankAccountForm';
import { CompanyBankAccount } from '../../../types/CompanyBankAccount';
import { Loader } from '../../../components/loader/Loader';
import { useTranslation } from '../../../context/translationContext';
import { paths } from '../../RouterContainer';
import { COMPANY_TABS } from '../Company/UserCompaniesUpdatePage';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { useLoading } from '../../../context/loadingContext';

/**
 * Page component for editing existing bank accounts of company
 */
export const CompanyBankAccountsUpdatePage = () => {
  const { companyId, id } = useParams();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyBankAccount | null>(null);
  const { startLoading, stopLoading } = useLoading();
  const { setBreadcrumbs } = useBreadcrumbs();
  const isCurrent = window.location.pathname.includes(`current`);
  useEffect(() => {
    setBreadcrumbs([
      {
        path: `${paths.MY_COMPANIES}`,
        name: 'navigation.my-companies',
      },
      {
        path: `${paths.MY_COMPANIES}/edit/${isCurrent ? `current` : companyId}`,
        name: 'navigation.company-setting',
      },
      {
        path: `${paths.MY_COMPANIES}/${isCurrent ? `current` : companyId}/${
          paths.COMPANY_BANK_ACCOUNTS
        }/edit/${id}`,
        name: 'navigation.company-bank-account-edit',
      },
    ]);
  }, []);

  /**
   * Back button action
   */
  const handleOnBack = () =>
    navigate(`/${paths.MY_COMPANIES}/edit/${isCurrent ? `current` : companyId}`, {
      state: { tab: COMPANY_TABS.BANK_ACCOUNTS },
    });

  /**
   * Loads company bank account
   */
  const fetchData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_BANK_ACCOUNT_ITEM}?id=${id}`,
    });
    setData(response?.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Handles edit action for new company bank account form submit.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPut({ url: apiUrl.COMPANY_BANK_ACCOUNT_UPDATE, data: values });
      if (response?.status === 200) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  if (data === null) return <Loader />;
  return (
    <div className="w-1/4	space-y-8">
      <h1 className="text-3xl font-bold">{t('page.bank-account.headline-edit')}</h1>
      <CompanyBankAccountForm
        onSubmit={handleSubmit}
        initialValues={data as Values}
        onBack={handleOnBack}
      />
    </div>
  );
};
